import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
import { EVENT_NAMES } from './events.js';
/**
 * Outbound webhooks (TS-958): an organisation subscribes a URL to events; the worker fans each
 * outbox event out to the matching subscriptions as deliveries and POSTs them, signed, retrying
 * with exponential backoff for 24 hours before dead-lettering. Per record, deliveries go in
 * order: a later event for the same record waits while an earlier one is still being retried.
 */
export const WEBHOOK_STATUSES = ['active', 'paused'];
export const webhookStatusSchema = z.enum(WEBHOOK_STATUSES);
/** pending: not tried yet; retrying: failed, will try again; succeeded; dead: gave up after the window. */
export const DELIVERY_STATUSES = ['pending', 'retrying', 'succeeded', 'dead'];
export const deliveryStatusSchema = z.enum(DELIVERY_STATUSES);
/** Request headers on every delivery. The signature covers `<timestamp>.<raw body>`. */
export const WEBHOOK_HEADERS = {
    signature: 'x-crm-signature',
    timestamp: 'x-crm-timestamp',
    event: 'x-crm-event',
    eventId: 'x-crm-event-id',
    delivery: 'x-crm-delivery',
};
/** How long a failing delivery is retried before it is dead-lettered. */
export const WEBHOOK_RETRY_WINDOW_MS = 24 * 60 * 60 * 1000;
const wildcardPrefix = (p) => p.startsWith('crm.') && p.endsWith('.*') && p.length > 6 ? p.slice(0, -1) : null;
/**
 * What a subscription listens to: an exact event name, every event of one record kind
 * (`crm.candidate.*`), or `*`. A pattern that matches nothing is refused, so a typo is a 400 at
 * setup rather than a subscription that never fires.
 */
export const webhookEventPatternSchema = z
    .string()
    .min(1)
    .max(100)
    .refine((p) => {
    if (p === '*')
        return true;
    if (EVENT_NAMES.includes(p))
        return true;
    const prefix = wildcardPrefix(p);
    return prefix !== null && EVENT_NAMES.some((n) => n.startsWith(prefix));
}, { message: 'an event name, crm.<record>.*, or *' });
export function matchesEvent(patterns, name) {
    return patterns.some((p) => {
        if (p === '*' || p === name)
            return true;
        const prefix = wildcardPrefix(p);
        return prefix !== null && name.startsWith(prefix);
    });
}
/**
 * Optional narrowing on the event payload: `{ field: [allowed values] }`, every field must
 * match. A field is looked up in `data` first, then at the top of the payload — so
 * `{ "entityType": ["jobs"] }` or `{ "to": ["<stage id>"] }` both work.
 */
export const webhookFilterSchema = z
    .record(z.string().min(1).max(64), z
    .array(z.union([z.string().max(200), z.number(), z.boolean()]))
    .min(1)
    .max(50))
    .refine((f) => Object.keys(f).length <= 10, { message: 'at most 10 fields' });
export function matchesFilter(filter, payload) {
    if (!filter)
        return true;
    const p = (payload ?? {});
    return Object.entries(filter).every(([field, allowed]) => {
        const value = p.data && field in p.data ? p.data[field] : p[field];
        return allowed.some((a) => a === value);
    });
}
const eventsSchema = z
    .array(webhookEventPatternSchema)
    .min(1)
    .max(50)
    .refine((e) => new Set(e).size === e.length, { message: 'each event once' });
export const webhookCreateSchema = z
    .object({
    url: z.url().max(2000),
    description: z.string().trim().max(200).default(''),
    events: eventsSchema,
    filter: webhookFilterSchema.nullable().default(null),
})
    .meta({
    examples: [
        {
            url: 'https://relay.remoteleverage.com/api/crm-webhooks',
            description: 'Relay task board',
            events: ['crm.job.status_changed', 'crm.candidate.stage_changed'],
            filter: null,
        },
    ],
});
export const webhookPatchSchema = z
    .object({
    url: z.url().max(2000),
    description: z.string().trim().max(200),
    events: eventsSchema,
    filter: webhookFilterSchema.nullable(),
    status: webhookStatusSchema,
})
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const webhookRotateSchema = z.object({
    /** Deliveries are signed with both secrets for this long, so the receiver can switch over. */
    graceMinutes: z
        .number()
        .int()
        .min(0)
        .max(7 * 24 * 60)
        .default(60),
});
export const webhookSchema = z.object({
    id: idSchema,
    url: z.string(),
    description: z.string(),
    events: z.array(z.string()),
    filter: webhookFilterSchema.nullable(),
    status: webhookStatusSchema,
    /** Last four characters of the current secret, to tell secrets apart. */
    secretHint: z.string(),
    previousSecretExpiresAt: instantSchema.nullable(),
    createdAt: instantSchema,
    updatedAt: instantSchema,
    /** Counts over the last 7 days, and the latest delivery. */
    stats: z.object({
        succeeded: z.number().int(),
        failing: z.number().int(),
        dead: z.number().int(),
        lastDeliveryAt: instantSchema.nullable(),
        lastStatus: deliveryStatusSchema.nullable(),
    }),
});
/** The create and rotate responses: the only time the secret is ever shown. */
export const webhookWithSecretSchema = webhookSchema.extend({ secret: z.string() });
export const webhookDeliverySchema = z.object({
    id: idSchema,
    subscriptionId: idSchema,
    eventId: idSchema,
    eventName: z.string(),
    status: deliveryStatusSchema,
    attempts: z.number().int(),
    lastStatusCode: z.number().int().nullable(),
    lastError: z.string().nullable(),
    createdAt: instantSchema,
    lastAttemptAt: instantSchema.nullable(),
    nextAttemptAt: instantSchema.nullable(),
    deliveredAt: instantSchema.nullable(),
    /** The delivery this one replays, if it is a replay. */
    replayOf: idSchema.nullable(),
    /** Exactly what is POSTed. */
    body: z.unknown(),
});
export const deliveryQuerySchema = z.object({
    status: deliveryStatusSchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * What a receiver gets (TS-958): the event name next to its data and changes, as the
 * acceptance criteria ask, plus the ids a receiver needs to dedupe (delivery is at least once:
 * dedupe on `id`, the event id, which a replay keeps).
 */
export const webhookBodySchema = z.object({
    id: idSchema,
    event: z.string(),
    occurredAt: instantSchema,
    organisationId: idSchema,
    entityType: z.string(),
    entityId: z.string(),
    actor: z.unknown(),
    data: z.record(z.string(), z.unknown()),
    changes: z.record(z.string(), z.unknown()).nullable(),
});
export function toWebhookBody(e) {
    const p = (e.payload ?? {});
    return {
        id: e.id,
        event: e.name,
        occurredAt: typeof e.occurredAt === 'string' ? e.occurredAt : e.occurredAt.toISOString(),
        organisationId: e.organisationId,
        entityType: p.entityType ?? '',
        entityId: p.id ?? '',
        actor: e.actor,
        data: p.data ?? {},
        changes: p.changes ?? null,
    };
}
const PRIVATE_V4 = [
    [0x0a000000, 8], // 10/8
    [0x7f000000, 8], // 127/8
    [0xa9fe0000, 16], // 169.254/16: link-local, and the cloud metadata endpoint
    [0xac100000, 12], // 172.16/12
    [0xc0a80000, 16], // 192.168/16
    [0x64400000, 10], // 100.64/10: carrier-grade NAT
    [0x00000000, 8], // 0/8
    [0xc6120000, 15], // 198.18/15: benchmarking
    [0xe0000000, 4], // 224/4: multicast
    [0xf0000000, 4], // 240/4: reserved and broadcast
];
function ipv4(host) {
    const parts = host.split('.');
    if (parts.length !== 4)
        return null;
    let n = 0;
    for (const part of parts) {
        if (!/^\d{1,3}$/.test(part))
            return null;
        const v = Number(part);
        if (v > 255)
            return null;
        n = n * 256 + v;
    }
    return n;
}
/**
 * Whether a webhook URL points somewhere internal (SSRF): loopback, private and link-local
 * ranges (the cloud metadata address included, also written as IPv4-mapped, IPv4-compatible,
 * NAT64 or 6to4 IPv6), multicast and reserved ranges, `localhost`, `.local`/`.internal` names. A
 * subscription to one is refused outside development and test, and the worker checks again
 * before every POST. A hostname that RESOLVES to a private address is not caught here; that
 * needs egress controls, which is the infrastructure's job.
 */
export function isPrivateHost(hostname) {
    // A trailing dot names the same host (`localhost.`).
    const h = hostname
        .toLowerCase()
        .replace(/^\[|\]$/g, '')
        .replace(/\.$/, '');
    if (h === 'localhost' ||
        h.endsWith('.localhost') ||
        h.endsWith('.local') ||
        h.endsWith('.internal'))
        return true;
    if (h.includes(':')) {
        const g = ipv6(h);
        // Not a literal the URL parser would accept: refuse rather than guess.
        if (!g)
            return true;
        const zeros = (n) => g.slice(0, n).every((x) => x === 0);
        if (zeros(5) && g[5] === 0xffff)
            return privateV4(g[6] * 0x10000 + g[7]);
        // ::ffff:0:0/96, IPv4-translated (SIIT): the IPv4 it reaches is the last 32 bits.
        if (zeros(4) && g[4] === 0xffff && g[5] === 0)
            return privateV4(g[6] * 0x10000 + g[7]);
        if (zeros(6))
            return true; // ::/96: unspecified, loopback and IPv4-compatible
        if (g[0] === 0x64 && g[1] === 0xff9b && g.slice(2, 6).every((x) => x === 0))
            return true;
        if (g[0] === 0x64 && g[1] === 0xff9b && g[2] === 1)
            return true; // 64:ff9b:1::/48, local-use NAT64
        if (g[0] === 0x2001 && g[1] === 0)
            return true; // 2001::/32, Teredo tunnels to any IPv4
        if (g[0] === 0x2002)
            return privateV4(g[1] * 0x10000 + g[2]); // 6to4 embeds an IPv4
        return ((g[0] & 0xfe00) === 0xfc00 ||
            (g[0] & 0xffc0) === 0xfe80 ||
            (g[0] & 0xffc0) === 0xfec0 ||
            (g[0] & 0xff00) === 0xff00);
    }
    const v4 = ipv4(h);
    return v4 !== null && privateV4(v4);
}
const privateV4 = (v4) => PRIVATE_V4.some(([base, bits]) => v4 >>> (32 - bits) === base >>> (32 - bits));
/** The eight groups of an IPv6 literal, with `::` expanded and a trailing dotted IPv4 split in two. */
function ipv6(host) {
    let h = host.split('%')[0];
    const dotted = /^(.*:)(\d+\.\d+\.\d+\.\d+)$/.exec(h);
    if (dotted) {
        const v4 = ipv4(dotted[2]);
        if (v4 === null)
            return null;
        h = `${dotted[1]}${(v4 >>> 16).toString(16)}:${(v4 & 0xffff).toString(16)}`;
    }
    const halves = h.split('::');
    if (halves.length > 2)
        return null;
    const groups = (part) => (part ? part.split(':') : []);
    const head = groups(halves[0]);
    const tail = halves.length === 2 ? groups(halves[1]) : [];
    const missing = 8 - head.length - tail.length;
    if (halves.length === 2 ? missing < 1 : missing !== 0)
        return null;
    const all = [...head, ...Array(missing).fill('0'), ...tail];
    if (!all.every((x) => /^[0-9a-f]{1,4}$/.test(x)))
        return null;
    return all.map((x) => parseInt(x, 16));
}
//# sourceMappingURL=webhooks.js.map